/**
 * Storefront client for the CDP experiences EDGE (`/e/v1/*`) + public survey
 * submit (`/s/:token`), write-key auth. Same rails the embeddable game-host uses
 * (`game-host/edge-client.ts`), but for the on-site overlay: it pulls pending
 * experiences for a surface, reports impression/click/dismiss/submitted telemetry,
 * and posts survey answers. All same-origin in the demo (nginx/vite proxy `/e/*`,
 * `/v1/events`, `/s/*` → engine), so no CORS and no session cookie — the public
 * write-key (`cdp_wk_…`, scope `experiences`) is the credential.
 */

/** Theme-safe experience projection as returned by `/e/v1/pending` (`toEdgeView`). */
export interface PendingExperience {
  experience_id: string;
  assignment_id?: string;
  assignment_receipt?: string;
  surface?: string;
  render_type: string;
  priority?: number;
  business_criticality?: string;
  content: Record<string, unknown> | null;
  frequency_cap?: unknown;
  session_cap?: number | null;
  client_rules?: {
    display?: { type?: string; delay_seconds?: number };
    dismiss_cooldown_seconds?: number;
    [key: string]: unknown;
  } | null;
  starts_at?: string | null;
  ends_at?: string | null;
  /** Тестовый показ по ссылке из админки (?cdp_preview=…): события сервер не записывает. */
  preview?: boolean;
}

const PREVIEW_STORAGE_KEY = "cdp_preview_token";

/**
 * Убрать ?cdp_preview=… из адресной строки после чтения: токен не должен оседать в истории браузера,
 * аналитике сайта (полный URL страницы) и заголовке Referer. На вкладке он живёт в sessionStorage.
 */
function stripPreviewParamFromUrl(): void {
  try {
    const h = globalThis.history as History | undefined;
    const href = (globalThis.location as { href?: string } | undefined)?.href;
    if (!h?.replaceState || !href) return;
    const url = new URL(href);
    if (!url.searchParams.has("cdp_preview")) return;
    url.searchParams.delete("cdp_preview");
    h.replaceState(h.state, "", url.pathname + url.search + url.hash);
  } catch {
    /* never break the host page */
  }
}


/** Interaction types the edge accepts on `POST /e/v1/event`. */
export type ExperienceEventType = "shown" | "clicked" | "dismissed" | "submitted";

export interface EdgeClientOptions {
  /** Edge base path/URL. Default `/e` (same-origin proxy in the demo). */
  base?: string;
  /** Public write-key (`cdp_wk_…`, scope `experiences`). */
  writeKey: string;
  /** Profile ref: `pid:<id>` (logged-in / anon-session) or `anon:<id>`; null = anonymous. */
  profileRef?: string | null;
  placement?: string | null;
  recipientToken?: string | null;
  /** Stable browser-tab session id used by edge session caps and telemetry. */
  sessionId?: string | null;
  /** Токен тестового показа (ссылка «Тестовый показ на сайте» из админки). */
  previewToken?: string | null;
  /** Base for the public survey submit endpoint (`/s/<code>`). Default "" (same-origin). */
  surveyBase?: string;
  fetchImpl?: typeof fetch;
}

export class ExperiencesEdgeClient {
  private base: string;
  private writeKey: string;
  private profileRef: string | null;
  private placement: string | null;
  private recipientToken: string | null;
  private sessionId: string | null;
  private previewToken: string | null;
  private surveyBase: string;
  private fetchImpl: typeof fetch;

  constructor(opts: EdgeClientOptions) {
    this.base = (opts.base ?? "/e").replace(/\/$/, "");
    this.writeKey = opts.writeKey;
    this.profileRef = opts.profileRef ?? null;
    this.placement = opts.placement?.trim() || null;
    this.recipientToken = opts.recipientToken?.trim() || null;
    this.sessionId = opts.sessionId?.trim() || null;
    this.previewToken = opts.previewToken?.trim() || null;
    this.surveyBase = (opts.surveyBase ?? "").replace(/\/$/, "");
    this.fetchImpl =
      opts.fetchImpl ??
      (globalThis.fetch ? globalThis.fetch.bind(globalThis) : (undefined as unknown as typeof fetch));
  }

  private headers(json: boolean, includeRecipient = true): Record<string, string> {
    return {
      Accept: "application/json",
      "X-CDP-Write-Key": this.writeKey,
      ...(includeRecipient && this.recipientToken ? { "X-CDP-Recipient-Token": this.recipientToken } : {}),
      ...(json ? { "Content-Type": "application/json" } : {}),
    };
  }

  setRecipientToken(token: string | null): void {
    this.recipientToken = token?.trim() || null;
  }

  /**
   * Токен тестового показа из адреса (?cdp_preview=…) — баг-лист Насти п.1: «pop-up должен отобразиться,
   * даже если вызвать его 10 раз». Хранится на вкладку (sessionStorage), чтобы пережить переходы по сайту.
   */
  static readPreviewToken(): string | null {
    try {
      const fromUrl = new URLSearchParams(globalThis.location?.search ?? "").get("cdp_preview");
      if (fromUrl && fromUrl.trim()) {
        try { globalThis.sessionStorage?.setItem(PREVIEW_STORAGE_KEY, fromUrl.trim()); } catch { /* ignore */ }
        stripPreviewParamFromUrl();
        return fromUrl.trim();
      }
      const stored = globalThis.sessionStorage?.getItem(PREVIEW_STORAGE_KEY);
      return stored && stored.trim() ? stored.trim() : null;
    } catch {
      return null;
    }
  }

  get inPreview(): boolean {
    return this.previewToken !== null;
  }

  private dropPreview(reason: string): void {
    this.previewToken = null;
    try { globalThis.sessionStorage?.removeItem(PREVIEW_STORAGE_KEY); } catch { /* ignore */ }
    try { console.warn(`[CDP] Тестовый показ по ссылке недоступен: ${reason}. Получите новую ссылку в админке.`); } catch { /* ignore */ }
  }

  /**
   * List pending experiences for a surface. `fresh=1` bypasses the edge's ~30s
   * cache so a just-fired event trigger / freshly-directed journey render shows up
   * immediately (the deployed SDK re-polls this way after a track()).
   */
  async pending(surface: string, opts: { fresh?: boolean } = {}): Promise<PendingExperience[]> {
    const qs = new URLSearchParams({ surface });
    if (this.profileRef) qs.set("profile", this.profileRef);
    if (this.placement) qs.set("placement", this.placement);
    if (this.sessionId) qs.set("session_id", this.sessionId);
    if (opts.fresh) qs.set("fresh", "1");
    const res = await this.fetchImpl(`${this.base}/v1/pending?${qs.toString()}`, {
      method: "GET",
      headers: { ...this.headers(false), ...(this.previewToken ? { "X-CDP-Preview-Token": this.previewToken } : {}) },
    });
    if (!res.ok) {
      if (res.status === 401 && this.previewToken) this.dropPreview("ссылка недействительна или истекла");
      return [];
    }
    const data = (await res.json().catch(() => ({}))) as {
      experiences?: PendingExperience[];
      preview?: { error?: string; message?: string };
    };
    if (data.preview?.error === "PREVIEW_LIMIT_REACHED") this.dropPreview("исчерпан лимит показов по ссылке");
    else if (data.preview?.error) {
      try { console.warn(`[CDP] Тестовый показ: ${data.preview.error}${data.preview.message ? ` — ${data.preview.message}` : ""}`); } catch { /* ignore */ }
    }
    return data.experiences ?? [];
  }

  /** Report an experience interaction (`shown`|`clicked`|`dismissed`|`submitted`). Fire-and-forget. */
  async event(input: {
    surface: string;
    experienceId: string;
    type: ExperienceEventType;
    payload?: Record<string, unknown>;
    idempotencyKey?: string;
    assignmentId?: string;
    assignmentReceipt?: string;
    /** Событие тестового показа — помечаем заголовком, сервер его не записывает. */
    preview?: boolean;
  }): Promise<boolean> {
    const body: Record<string, unknown> = {
      surface: input.surface,
      experience_id: input.experienceId,
      type: input.type,
    };
    if (this.profileRef) body.profile = this.profileRef;
    if (this.sessionId) body.session_id = this.sessionId;
    if (input.payload) body.payload = input.payload;
    if (input.assignmentId && input.assignmentReceipt) {
      body.assignment_id = input.assignmentId;
      body.assignment_receipt = input.assignmentReceipt;
    }
    const idempotencyKey = input.idempotencyKey ?? (input.assignmentId ? `${input.assignmentId}:${input.type}` : null);
    if (idempotencyKey) body.idempotency_key = idempotencyKey;
    const res = await this.fetchImpl(`${this.base}/v1/event`, {
      method: "POST",
      headers: { ...this.headers(true, false), ...(input.preview ? { "X-CDP-Preview-Token": this.previewToken ?? "expired" } : {}) },
      body: JSON.stringify(body),
    });
    return res.ok;
  }

  /**
   * Submit a survey answer to the public endpoint `POST /s/<code>` (no write-key —
   * the code IS the auth). `code` = the experience's injected `content.response_token`.
   * NOTE: we deliberately use the token + our own same-origin `/s/` proxy rather than
   * the injected `content.submit_url`, which is an absolute admin-domain `/api/s/…`
   * URL that isn't reachable from the storefront origin.
   */
  async submitSurvey(
    code: string,
    body: { score: number; comment?: string | null; extra_answers?: Record<string, unknown> | null },
  ): Promise<{ ok: boolean; bucket?: string; status: number }> {
    const res = await this.fetchImpl(`${this.surveyBase}/s/${encodeURIComponent(code)}`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; bucket?: string };
    return { ok: res.ok && data.ok === true, bucket: data.bucket, status: res.status };
  }
}
